import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { Play, ArrowUpRight, FileDown, Mail } from "lucide-react";
import { MaskLine } from "./Motion";

export default function Hero({ content, onPlayFeatured, onContact, onDownloadResume }) {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
    const portraitY = useTransform(scrollYProgress, [0, 1], ["0%", "20%"]);
    const portraitScale = useTransform(scrollYProgress, [0, 1], [1, 1.08]);
    const textY = useTransform(scrollYProgress, [0, 1], ["0%", "-20%"]);

    const athlete = content.athlete;
    const featured = content.featuredVideo;

    return (
        <section id="hero" ref={ref} className="relative min-h-[100svh] pt-20 md:pt-24 overflow-hidden">
            {/* Backdrop grid + purple glow */}
            <div className="absolute inset-0 grid-lines opacity-40 pointer-events-none" />
            <div className="absolute -left-40 top-1/3 w-[700px] h-[700px] radial-purple pointer-events-none" />
            <div className="absolute right-0 bottom-0 w-[500px] h-[500px] radial-gold opacity-60 pointer-events-none" />

            <div className="max-w-[1600px] mx-auto px-5 md:px-10 relative z-10">
                {/* Top meta strip */}
                <motion.div
                    className="flex items-center justify-between font-mono text-[10px] md:text-xs tracking-[0.25em] uppercase text-white/60 pt-2 pb-6 border-b border-white/10"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.4 }}
                    data-testid="hero-meta"
                >
                    <span>
                        <span className="text-brand-gold">●</span> {athlete.recruitingStatus}
                    </span>
                    <span className="hidden md:inline">FILE / 2026-2027</span>
                    <span>{athlete.location}</span>
                </motion.div>

                <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 md:gap-10 mt-8 md:mt-14">
                    {/* Text column */}
                    <motion.div style={{ y: textY }} className="lg:col-span-7 order-2 lg:order-1">
                        <div className="chapter-num mb-4">CH. 01 / THE ATHLETE</div>

                        <h1
                            className="font-mono uppercase tracking-tighter leading-[0.85] text-[16vw] sm:text-[13vw] lg:text-[9.5vw]"
                            data-testid="hero-name"
                        >
                            <MaskLine delay={0.05}>JAI&apos;REL</MaskLine>
                            <MaskLine delay={0.18} className="text-brand-gold">
                                YOUNG<span className="editorial text-white/70 text-[0.5em] normal-case">.</span>
                            </MaskLine>
                        </h1>

                        <div className="mt-6 max-w-2xl">
                            <MaskLine delay={0.35} className="font-mono text-xs md:text-sm tracking-[0.2em] uppercase text-white/70">
                                {athlete.tagline} <span className="text-brand-purple mx-2">/</span>{" "}
                                <span className="editorial text-brand-gold text-lg not-italic md:text-xl">
                                    Class of {athlete.graduationYear}
                                </span>
                            </MaskLine>
                        </div>

                        {/* Data quick strip */}
                        <motion.div
                            className="grid grid-cols-2 md:grid-cols-4 gap-px mt-8 border border-white/10 bg-white/[0.02]"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ delay: 0.5 }}
                            data-testid="hero-quickinfo"
                        >
                            {[
                                { k: "Weight", v: `${athlete.currentWeight} lbs` },
                                { k: "GPA", v: athlete.gpa, gold: true },
                                { k: "Height", v: athlete.height },
                                { k: "School", v: "Corinth Holders" },
                            ].map((it) => (
                                <div key={it.k} className="p-4 bg-black">
                                    <div className="chapter-num">{it.k}</div>
                                    <div
                                        className={`font-mono text-2xl md:text-3xl mt-2 ${
                                            it.gold ? "text-brand-gold" : "text-white"
                                        }`}
                                    >
                                        {it.v}
                                    </div>
                                </div>
                            ))}
                        </motion.div>

                        {/* CTAs */}
                        <motion.div
                            className="mt-8 flex flex-wrap gap-3"
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.6 }}
                        >
                            <button className="btn-gold" onClick={onPlayFeatured} data-testid="cta-highlight">
                                <Play size={14} fill="black" /> Watch Highlight Reel
                            </button>
                            <button
                                className="btn-outline"
                                onClick={() => document.getElementById("profile")?.scrollIntoView({ behavior: "smooth" })}
                                data-testid="cta-profile"
                            >
                                View Wrestling Profile <ArrowUpRight size={14} />
                            </button>
                            <button className="btn-outline" onClick={onContact} data-testid="cta-contact">
                                <Mail size={14} /> Contact Me
                            </button>
                            <button className="btn-outline" onClick={onDownloadResume} data-testid="cta-resume">
                                <FileDown size={14} /> Recruiting Résumé
                            </button>
                        </motion.div>
                    </motion.div>

                    {/* Portrait column */}
                    <motion.div
                        className="lg:col-span-5 order-1 lg:order-2 relative"
                        initial={{ opacity: 0, scale: 1.05 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
                    >
                        <div className="absolute inset-0 -m-8 radial-purple" />
                        <motion.div
                            style={{ y: portraitY, scale: portraitScale }}
                            className="relative aspect-[3/4] overflow-hidden border border-white/10"
                        >
                            <img
                                src={content.portraits.hero}
                                alt={`${athlete.firstName} ${athlete.lastName}`}
                                className="w-full h-full object-cover"
                                data-testid="hero-portrait"
                            />
                            {/* Overlays */}
                            <div className="absolute top-4 left-4 font-mono text-[10px] tracking-[0.25em] uppercase text-brand-gold bg-black/60 backdrop-blur px-3 py-1 border border-brand-gold/40">
                                Recruit / 132 LB
                            </div>
                            <div className="absolute bottom-4 left-4 right-4 flex items-end justify-between">
                                <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-white/80">
                                    Corinth Holders HS <span className="text-white/40">/</span> Wendell NC
                                </div>
                                <div className="font-mono text-[10px] text-white/50">01/03</div>
                            </div>
                        </motion.div>

                        {/* Featured video preview beside portrait */}
                        <button
                            onClick={onPlayFeatured}
                            className="mt-4 w-full group border border-white/10 hover:border-brand-gold/70 transition-colors bg-black text-left"
                            data-testid="hero-featured-preview"
                        >
                            <div className="flex items-center gap-3 p-3">
                                <div className="w-12 h-12 bg-brand-gold flex items-center justify-center shrink-0">
                                    <Play size={16} fill="black" />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="chapter-num">Featured Reel</div>
                                    <div className="font-mono text-sm truncate">{featured.title}</div>
                                </div>
                                <ArrowUpRight size={16} className="text-white/50 group-hover:text-brand-gold" />
                            </div>
                        </button>
                    </motion.div>
                </div>
            </div>
        </section>
    );
}
